import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { getDomainLanguage } from "./utils";

// Import common translations that should always be available
import commonEN from "../locales/en/common.json";
import commonFR from "../locales/fr/common.json";
import headerEN from "../locales/en/header.json";
import headerFR from "../locales/fr/header.json";
import footerEN from "../locales/en/footer.json";
import footerFR from "../locales/fr/footer.json";
import bookingEN from "../locales/en/booking.json";
import bookingFR from "../locales/fr/booking.json";
import galleryEN from "../locales/en/gallery.json";
import galleryFR from "../locales/fr/gallery.json";
import homeEN from "../locales/en/home.json";
import homeFR from "../locales/fr/home.json";
import newsletterEN from "../locales/en/newsletter.json";
import newsletterFR from "../locales/fr/newsletter.json";
import cleanerEN from "../locales/en/cleaner.json";
import cleanerFR from "../locales/fr/cleaner.json";
import profileEN from "../locales/en/profile.json";
import profileFR from "../locales/fr/profile.json";
import calendarEN from "../locales/en/calendar.json";
import calendarFR from "../locales/fr/calendar.json";
import recommendationsEN from "../locales/en/recommendations.json";
import recommendationsFR from "../locales/fr/recommendations.json";
import reviewsEN from "../locales/en/reviews.json";
import reviewsFR from "../locales/fr/reviews.json";
import authEN from "../locales/en/auth.json";
import authFR from "../locales/fr/auth.json";
import propertiesEN from "../locales/en/properties.json";
import propertiesFR from "../locales/fr/properties.json";
import onboardingEN from "../locales/en/onboarding.json";
import onboardingFR from "../locales/fr/onboarding.json";
import landingEN from "../locales/en/landing.json";
import landingFR from "../locales/fr/landing.json";
import messagesEN from "../locales/en/messages.json";
import messagesFR from "../locales/fr/messages.json";
import accountEN from "../locales/en/account.json";
import accountFR from "../locales/fr/account.json";
import discountPopupEN from "../locales/en/discount-popup.json";
import discountPopupFR from "../locales/fr/discount-popup.json";
import cookiesEN from "../locales/en/cookies.json";
import cookiesFR from "../locales/fr/cookies.json";
import leadMagnetEN from "../locales/en/lead-magnet.json";
import leadMagnetFR from "../locales/fr/lead-magnet.json";
import appsEN from "../locales/en/apps.json";
import appsFR from "../locales/fr/apps.json";
import companyEN from "../locales/en/company.json";
import companyFR from "../locales/fr/company.json";
// Initialize with only the common namespace preloaded
const resources = {
  en: {
    common: commonEN,
    header: headerEN,
    footer: footerEN,
    booking: bookingEN,
    cleaner: cleanerEN,
    gallery: galleryEN,
    home: homeEN,
    newsletter: newsletterEN,
    profile: profileEN,
    calendar: calendarEN,
    recommendations: recommendationsEN,
    reviews: reviewsEN,
    auth: authEN,
    properties: propertiesEN,
    onboarding: onboardingEN,
    landing: landingEN,
    messages: messagesEN,
    account: accountEN,
    "discount-popup": discountPopupEN,
    cookies: cookiesEN,
    "lead-magnet": leadMagnetEN,
    apps: appsEN,
    company: companyEN,
  },
  fr: {
    common: commonFR,
    header: headerFR,
    footer: footerFR,
    booking: bookingFR,
    cleaner: cleanerFR,
    gallery: galleryFR,
    home: homeFR,
    newsletter: newsletterFR,
    profile: profileFR,
    calendar: calendarFR,
    recommendations: recommendationsFR,
    reviews: reviewsFR,
    auth: authFR,
    properties: propertiesFR,
    onboarding: onboardingFR,
    landing: landingFR,
    messages: messagesFR,
    account: accountFR,
    "discount-popup": discountPopupFR,
    cookies: cookiesFR,
    "lead-magnet": leadMagnetFR,
    apps: appsFR,
    company: companyFR,
  },
};

// Custom domain detector
const domainDetector = {
  name: "domain",
  lookup() {
    return getDomainLanguage();
  },
  cacheUserLanguage(lng: string) {
    // Don't cache domain-based language - it should always be redetected
  },
};

i18n
  .use(Backend)
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources,
    fallbackLng: "fr", // French as default
    supportedLngs: ["fr", "en"],
    debug: process.env.NODE_ENV === "development",
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      transKeepBasicHtmlNodesFor: [
        "br",
        "strong",
        "i",
        "em",
        "b",
        "p",
        "span",
        "a",
        "ul",
        "li",
      ],
    },
    detection: {
      order: ["domain", "localStorage", "navigator"],
      caches: ["localStorage"],
    },
    // Configure namespaces
    ns: [
      "common",
      "header",
      "footer",
      "booking",
      "gallery",
      "home",
      "newsletter",
      "cleaner",
      "profile",
      "calendar",
      "recommendations",
      "reviews",
      "auth",
      "properties",
      "onboarding",
      "landing",
      "messages",
      "account",
      "discount-popup",
      "cookies",
      "lead-magnet",
      "apps",
      "company",
    ],
    defaultNS: "common",
    fallbackNS: "common",
    backend: {
      // Path to load resources from
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    // Enable partitioning of translations
    partialBundledLanguages: true,
  });

// Add the custom detector
i18n.services.languageDetector.addDetector(domainDetector);

export default i18n;
